/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Pribyl (pribyl@cadwork.cz)
 *
 */

import BufferHandler from "App/render/webgl/utils/buffer_handler";
import MemoryManagement from "App/memory_management";

export default
class MultiArrayBufferHandler extends BufferHandler {

    public static maxVerticesCount: number = 65535;
    public static helperArrayBufferSize: number = 1024 * 1024 * 5 * 4; // 5MB cache buffer
    public static helperArrayBuffer: ArrayBuffer = new ArrayBuffer(MultiArrayBufferHandler.helperArrayBufferSize);

    public members: any[] = [];

    constructor(aBatch) {
        super(aBatch);
    }

    public release() {
        super.release();

        this.members = [];
    }

    public canHandleVertices(aVerticesToHandle: number) {
        if (this.members.length === 0) {
            return false;
        }

        return aVerticesToHandle <= this.members[0].size;
    }

    public reCreateBuffer(aMembers: number[][]) {
        this.release();
        this.createBuffer(aMembers);
    }

    public createBuffer(aMembers: number[][]) {
        let gl = this._context.getWebGLRenderingContext();

        let offset = 0;
        for (let i = 0; i < aMembers.length; i++) {
            this.members.push({
                size: aMembers[i][0],
                attributeSize: aMembers[i][1],
                filled: 0,
                offset: offset,
                byteLength: aMembers[i][0] * aMembers[i][1] * 4
            });

            offset += this.members[i].byteLength;
        }

        this.id = gl.createBuffer();
        this.bytesSize = offset;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.id);
        if (MultiArrayBufferHandler.helperArrayBufferSize < offset) {
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(offset / 4), gl.DYNAMIC_DRAW);
        } else {
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(MultiArrayBufferHandler.helperArrayBuffer, 0, offset / 4), gl.DYNAMIC_DRAW);
        }

        MemoryManagement.addBufferObject(this.bytesSize);
    }

    public setAndGetNextFreePosition(aData: Float32Array, aMemberIndex: number) {
        let member = this.members[aMemberIndex];

        let addedVerticesCount = aData.length / member.attributeSize;
        member.filled += addedVerticesCount;
        return member.filled - addedVerticesCount;
    }

    public update(aData: BufferSource, aVertexOffset: number, aMemberIndex: number) {
        let gl = this._context.getWebGLRenderingContext();

        // handle the case when the bufferSubData is called before the bufferData is called so the size of the buffer is not defined (e.g. when adding primitive point,
        // the position update can arrive before the buffer is created).
        if (this.bytesSize > 0) {
            gl.bufferSubData(gl.ARRAY_BUFFER, this.members[aMemberIndex].offset + aVertexOffset * this.members[aMemberIndex].attributeSize * 4, aData); // * 4 because of the Float32Array used int _createBuffer
        }
    }

    public bindBuffer() {
        let gl = this._context.getWebGLRenderingContext();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.id);
    }

    public unbindBuffer() {
        let gl = this._context.getWebGLRenderingContext();
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
    }
}
